import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ChevronDownIcon from 'mdi-react/ChevronDownIcon';
import ChevronUpIcon from 'mdi-react/ChevronUpIcon';
import DeleteIcon from 'mdi-react/DeleteIcon';
import { array, func } from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { I18n } from 'utils/i18n';
import authEmployee from 'store/selectors/authEmployee';
import { useSearchEmailContactsQuery } from 'store/app/entities/CompaniesSlice';

import { validateExternalRecipients } from './validateExternalRecipients';

const MAX_RECIPIENTS = 50;

let rowId = 0;
const nextId = () => {
  rowId += 1;
  return `rec-${rowId}`;
};

const StepRecipients = ({ recipients, setRecipients, setNextEnabled, setError }) => {
  const employee = useSelector(authEmployee);
  const [emailSuggestQ, setEmailSuggestQ] = useState('');
  const { data: suggestData } = useSearchEmailContactsQuery(
    { q: emailSuggestQ, limit: 15 },
    { skip: !emailSuggestQ || emailSuggestQ.length < 2 },
  );
  const suggestions = suggestData?.contacts || [];

  const validateRows = useCallback(
    rows => {
      const { ok, reason } = validateExternalRecipients(rows);
      if (!ok) {
        const reasonToKey = {
          atLeastOneSigner: 'HrBot.RecipientsStep.atLeastOneSigner',
          emailInvalid: 'HrBot.RecipientsStep.emailInvalid',
          phoneInvalid: 'HrBot.RecipientsStep.phoneInvalid',
          signerEmailRequired: 'HrBot.RecipientsStep.signerEmailRequired',
        };
        const key = reasonToKey[reason] || reasonToKey.signerEmailRequired;
        setError(I18n.t(key));
        setNextEnabled(false);
        return;
      }
      setError(null);
      setNextEnabled(true);
    },
    [setError, setNextEnabled],
  );

  useEffect(() => {
    if (!recipients?.length) {
      setRecipients([
        {
          id: nextId(),
          email: '',
          phone: '',
          name: '',
          department: '',
          jobTitle: '',
          code: '',
          action: 'signer',
          order: 0,
        },
      ]);
    }
  }, [recipients, setRecipients]);

  useEffect(() => {
    if (recipients?.length) {
      validateRows(recipients);
    }
  }, [recipients, validateRows]);

  const sorted = useMemo(
    () => [...(recipients || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [recipients],
  );

  const updateRow = (id, patch) => {
    setRecipients(rows => rows.map(r => (r.id === id ? { ...r, ...patch } : r)));
  };

  const removeRow = id => {
    setRecipients(rows => {
      const next = rows.filter(r => r.id !== id);
      return next.length
        ? next
        : [
            {
              id: nextId(),
              email: '',
              phone: '',
              name: '',
              department: '',
              jobTitle: '',
              code: '',
              action: 'signer',
              order: 0,
            },
          ];
    });
  };

  const move = (id, dir) => {
    setRecipients(rows => {
      const ordered = [...rows].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const i = ordered.findIndex(r => r.id === id);
      const j = i + dir;
      if (j < 0 || j >= ordered.length) return rows;
      const a = ordered[i];
      const b = ordered[j];
      const ao = a.order ?? i;
      const bo = b.order ?? j;
      return rows.map(r => {
        if (r.id === a.id) return { ...r, order: bo };
        if (r.id === b.id) return { ...r, order: ao };
        return r;
      });
    });
  };

  const addRow = () => {
    if ((recipients || []).length >= MAX_RECIPIENTS) {
      setError(I18n.t('HrBot.RecipientsStep.maxRecipients', { max: MAX_RECIPIENTS }));
      return;
    }
    const maxOrder = Math.max(0, ...(recipients || []).map(r => r.order ?? 0));
    setRecipients(rows => [
      ...rows,
      {
        id: nextId(),
        email: '',
        phone: '',
        name: '',
        department: '',
        jobTitle: '',
        code: '',
        action: 'signer',
        order: maxOrder + 1,
      },
    ]);
  };

  const addMe = () => {
    const email = employee?.email || '';
    if ((recipients || []).length >= MAX_RECIPIENTS) {
      setError(I18n.t('HrBot.RecipientsStep.maxRecipients', { max: MAX_RECIPIENTS }));
      return;
    }
    setRecipients(rows => {
      const emptyIdx = rows.findIndex(r => !String(r.email || '').trim());
      if (emptyIdx >= 0) {
        return rows.map((r, i) =>
          i === emptyIdx
            ? {
                ...r,
                email,
                phone: employee?.user?.phone || '',
                name: [employee.firstName, employee.surName].filter(Boolean).join(' '),
                department: employee?.department || '',
                jobTitle: employee?.jobTitle || '',
                code: employee?.code || '',
                action: 'signer',
              }
            : r,
        );
      }
      const maxOrder = Math.max(0, ...rows.map(r => r.order ?? 0));
      return [
        ...rows,
        {
          id: nextId(),
          email,
          phone: employee?.user?.phone || '',
          name: [employee.firstName, employee.surName].filter(Boolean).join(' '),
          department: employee?.department || '',
          jobTitle: employee?.jobTitle || '',
          code: employee?.code || '',
          action: 'signer',
          order: maxOrder + 1,
        },
      ];
    });
  };

  return (
    <Box sx={{ px: 2 }}>
      <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="none" />
              <TableCell sx={{ width: 36, textAlign: 'center' }}>#</TableCell>
              <TableCell>{I18n.t('HrBot.RecipientsStep.email')}</TableCell>
              <TableCell>{I18n.t('HrBot.RecipientsStep.phone')}</TableCell>
              <TableCell>{I18n.t('HrBot.RecipientsStep.name')}</TableCell>
              <TableCell>{I18n.t('HrBot.RecipientsStep.department')}</TableCell>
              <TableCell>{I18n.t('HrBot.RecipientsStep.jobTitle')}</TableCell>
              <TableCell>{I18n.t('HrBot.RecipientsStep.code')}</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((row, idx) => (
              <TableRow key={row.id}>
                <TableCell padding="none">
                  <IconButton size="small" onClick={() => move(row.id, -1)} disabled={idx === 0}>
                    <ChevronUpIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => move(row.id, 1)}
                    disabled={idx === sorted.length - 1}
                  >
                    <ChevronDownIcon />
                  </IconButton>
                </TableCell>
                <TableCell>{idx + 1}</TableCell>
                <TableCell>
                  <Autocomplete
                    freeSolo
                    options={suggestions}
                    filterOptions={opts => opts}
                    getOptionLabel={option => {
                      if (typeof option === 'string') return option;
                      return option.email || '';
                    }}
                    isOptionEqualToValue={(a, b) => {
                      const ae = typeof a === 'string' ? a : a?.email;
                      const be = typeof b === 'string' ? b : b?.email;
                      return ae === be;
                    }}
                    value={row.email}
                    onChange={(e, newValue) => {
                      if (typeof newValue === 'string') {
                        updateRow(row.id, { email: newValue });
                      } else if (newValue?.email) {
                        updateRow(row.id, {
                          email: newValue.email,
                          name: newValue.name || '',
                          phone: newValue.phone || '',
                          department: newValue.department || '',
                          jobTitle: newValue.jobTitle || '',
                          code: newValue.code || '',
                        });
                      }
                    }}
                    onInputChange={(e, newInput, reason) => {
                      if (reason === 'input' || reason === 'clear') {
                        setEmailSuggestQ(newInput);
                        updateRow(row.id, { email: newInput });
                      }
                    }}
                    renderOption={(props, option) => (
                      <li {...props} key={option._id ? String(option._id) : option.email}>
                        <Box>
                          <Typography variant="body2">{option.email}</Typography>
                          {(option.name || option.phone) && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                display: 'block',
                              }}
                            >
                              {[option.name, option.phone].filter(Boolean).join(' · ')}
                            </Typography>
                          )}
                        </Box>
                      </li>
                    )}
                    renderInput={params => (
                      <TextField {...params} size="small" placeholder="email@example.com" />
                    )}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={row.phone || ''}
                    onChange={e => updateRow(row.id, { phone: e.target.value })}
                    placeholder="+34600111222"
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={row.name || ''}
                    onChange={e => updateRow(row.id, { name: e.target.value })}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={row.department || ''}
                    onChange={e => updateRow(row.id, { department: e.target.value })}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={row.jobTitle || ''}
                    onChange={e => updateRow(row.id, { jobTitle: e.target.value })}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={row.code || ''}
                    onChange={e => updateRow(row.id, { code: e.target.value })}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => removeRow(row.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
        <Button variant="outlined" onClick={addMe}>
          {I18n.t('HrBot.RecipientsStep.addMe')}
        </Button>
        <Button variant="outlined" onClick={addRow}>
          {I18n.t('HrBot.RecipientsStep.addSigner')}
        </Button>
      </Box>
    </Box>
  );
};

StepRecipients.propTypes = {
  recipients: array.isRequired,
  setRecipients: func.isRequired,
  setNextEnabled: func.isRequired,
  setError: func.isRequired,
};

export default StepRecipients;
